import React, { useEffect, useLayoutEffect } from 'react';
import 'styles/main-layout.scss';
import { ArrowLeftIcon, AlertOctagonIcon } from '@/assets/icons/icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import useNativeMessaging from '@/hooks/useNativeMessaging';
import invalidQuery from '@/utils/invalidQuery';
import { FaRegStar } from 'react-icons/fa';
import api from '@/utils/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FaStar } from 'react-icons/fa';
import { useState } from 'react';
import Auth from '@/utils/auth';
import { update as updateTokenReady } from '@/store/tokenReady';
import { useDispatch } from 'react-redux';
import { update as updateComchatLabLoading } from '@/store/comchatLabLoadingSlice';
import { useMutation } from '@tanstack/react-query';
import url from '@/utils/url';
import axios from 'axios';
import { setDefaultSentryTag } from '@/utils/sentry';

const topLevelRoutes = ['/', '/login', '/test']; // 앱의 주요 루트

export default function MainLayout({ children }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isLoginPage = pathname === '/login';
    const companyId = pathname.startsWith('/company-view/')
        ? pathname.replace('/company-view/', '').split('/')[0]
        : null;
    const companyName = useSelector((state) => state.company.companyInfo?.name); // Redux에서 로고 가져오기
    const isCompanyViewPage = pathname.startsWith('/company-view');
    const isLabPage = pathname.startsWith('/comchat-lab');
    const isSharePage = pathname.startsWith('/share');
    const isStatDetailPage = pathname.startsWith('/stat-detail');
    const isBusinessCardListPage = pathname.startsWith('/business-card-list');
    const isBusinessCardDetailPage = pathname.startsWith('/business-card-detail');
    const isBusinessCardEditPage = pathname.startsWith('/business-card-edit');
    const tokenReady = useSelector((state) => state.tokenReady);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const { postMessageToNative } = useNativeMessaging({
        handlers: {
            SET_TOKEN: async (data) => {
                const { token } = data?.detail || {};
                const appVersion = data?.detail?.appVersion;
                // console.log(Auth.isLoggedIn(), Auth.getToken());
                if (token) {
                    Auth.login(token);
                    await mutateAsyncCheckValidToken();
                    dispatch(updateTokenReady(true));
                    postMessageToNative({
                        from: 'SET_TOKEN_SUCCESS',
                        message: 'success',
                    });
                    setDefaultSentryTag(token, appVersion);
                } else if (Auth.isLoggedIn()) {
                    await mutateAsyncCheckValidToken();
                    dispatch(updateTokenReady(true));
                    postMessageToNative({
                        from: 'SET_TOKEN_SUCCESS',
                        message: 'success',
                    });
                } else {
                    postMessageToNative({
                        from: 'SET_TOKEN_SUCCESS',
                        message: 'fail',
                    });
                }
            },
            message: (data) => {
                if (data?.from !== 'NATIVE') return;

                if (data?.type === 'LOAD_COMCHATLAB') {
                    const loading = data?.loading;
                    dispatch(updateComchatLabLoading(loading));
                }
                if (data?.type === 'CHAT_GROUP_ID') {
                    console.log('CHAT_GROUP_ID', data.data);
                    queryClient.invalidateQueries(['conversationHistory', data.data]);
                    let id = data.data || '';
                    if (id == 'null') id = '';
                    navigate(`/?groupId=${id}`, { replace: true });
                }
            },
            CHECK_VALID_TOKEN: async () => {
                await mutateAsyncCheckValidToken();
            },
        },
    });
    const isReactNative = window?.ReactNativeWebView;
    const location = useLocation();

    async function checkValidToken() {
        const res = await axios.get(url + '/auth/validToken', {
            headers: {
                Authorization: `Bearer ${Auth.getToken()}`,
            },
        });
        return res.data;
    }
    const { mutate: mutateCheckValidToken, mutateAsync: mutateAsyncCheckValidToken } = useMutation({
        mutationFn: checkValidToken,
        onSuccess: (data) => {
            console.log('valid token');
        },
        onError: (error) => {
            console.log(error);
            postMessageToNative({
                from: 'SET_TOKEN_SUCCESS',
                message: 'fail',
            });
        },
    });

    const goBack = () => {
        navigate(-1);
    };

    const sendMessageToNative = () => {
        if (isReactNative) {
            postMessageToNative({ from: 'ERROR_REPORT' });
        }
    };

    async function addBookmark(id) {
        const res = await api.post(`/company/bookmark/${id}`);
        return res.data;
    }
    async function removeBookmark(id) {
        const res = await api.post(`/company/unbookmark/${id}`);
        return res.data;
    }

    async function getIsBookmarkCompany(id) {
        const res = await api.get(`/company/bookmark/${id}`);
        return res.bookmark;
    }

    const { data: isBookmarkCompany } = useQuery({
        queryKey: ['isBookmarkCompany', companyId],
        queryFn: () => getIsBookmarkCompany(companyId),
        enabled: !!companyId && isCompanyViewPage && !isLabPage,
    });

    async function handleBookmark() {
        if (isBookmarkCompany) {
            await removeBookmark(companyId);
            invalidQuery({ queryKey: ['isBookmarkCompany', companyId] });
        } else {
            await addBookmark(companyId);
            invalidQuery({ queryKey: ['isBookmarkCompany', companyId] });
        }
    }

    const showBackButton =
        !topLevelRoutes.includes(pathname) &&
        !isCompanyViewPage &&
        !isLabPage &&
        !isStatDetailPage &&
        !isSharePage &&
        !isBusinessCardDetailPage &&
        !isBusinessCardEditPage &&
        !isBusinessCardListPage;

    useLayoutEffect(() => {
        if (Auth.isLoggedIn()) {
            dispatch(updateTokenReady(true));
        }
    }, []);

    useEffect(() => {
        if (!window?.ReactNativeWebView) return;
        if (tokenReady || isLoginPage || isSharePage) return;

        const requestToken = () => {
            postMessageToNative({ from: 'TOKEN_REQUEST' });
        };

        requestToken();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !Auth.isLoggedIn()) {
                requestToken();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [postMessageToNative, tokenReady, isLoginPage, isSharePage]);

    if (!tokenReady && !isLoginPage && !isSharePage) {
        return null;
    }

    return (
        <div className={classNames('layout-container', { 'company-view': pathname.startsWith('/company-view') })}>
            <div className="layout">
                {/* {showBackButton && (
                    <div className="main-header">
                        <div onClick={goBack}>
                            <ArrowLeftIcon color="var(--textBase)" />
                        </div>
                        <div className="company-name">{companyName}</div>
                        {isReactNative ? (
                            isCompanyViewPage ? (
                                <div onClick={handleBookmark}>
                                    {isBookmarkCompany ? (
                                        <FaStar color="var(--textBase)" width={24} height={24} />
                                    ) : (
                                        <FaRegStar color="var(--textBase)" width={24} height={24} />
                                    )}
                                </div>
                            ) : (
                                isCompanyViewPage
                                ?
                                <div></div>
                                :
                                <div onClick={sendMessageToNative}>
                                    <AlertOctagonIcon color="var(--textBase)" width={20} height={20} />
                                </div>
                            )
                        ) : (
                            <div className="right-placeholder" />
                        )}
                    </div>
                )} */}
                <div
                    className={classNames('main-content', {
                        'exist-header': showBackButton,
                    })}
                >
                    {children}
                </div>
                {/* {pathname !== '/login' && <Footer />} */}
            </div>
        </div>
    );
}
